import React, { useState } from 'react';
import { SparklesIcon, PlayIcon, ShieldCheckIcon, StarIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function Hero() {
  const [greetingPlayed, setGreetingPlayed] = useState(false);
  const [kikiState, setKikiState] = useState('/assets/images/kiki-real.png');

  const handleKikiGreeting = () => {
    sounds.playSound('voice_count');
    setKikiState('/assets/images/kiki-cheer.png');
    setGreetingPlayed(true);
    setTimeout(() => {
      setKikiState('/assets/images/kiki-real.png');
    }, 3500);
  };

  return (
    <section className="relative overflow-hidden pt-8 pb-16 lg:pt-16 lg:pb-24 bg-gradient-to-b from-[#FFFDF9] via-kiki-sky/30 to-white">
      {/* Decorative floating clouds & shapes */}
      <div className="absolute top-10 left-6 text-kiki-blue/20 animate-drift pointer-events-none select-none text-6xl">
        ☁️
      </div>
      <div className="absolute top-28 right-12 text-kiki-pink/20 animate-drift pointer-events-none select-none text-7xl" style={{ animationDelay: '2s' }}>
        ☁️
      </div>
      <div className="absolute top-48 left-1/3 text-kiki-yellow/40 animate-pulse pointer-events-none select-none text-3xl">
        ⭐
      </div>
      <div className="absolute top-16 right-1/4 text-kiki-green/40 animate-bounce-slow pointer-events-none select-none text-2xl">
        🎈
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Headline and Badges */}
          <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
            
            {/* Top pill badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-kiki-yellow shadow-sm text-slate-800 text-xs sm:text-sm font-heading font-bold">
              <span className="flex h-2.5 w-2.5 rounded-full bg-kiki-coral animate-ping" />
              <span>Welcoming Preschoolers &amp; Early Learners (Ages 2–8)</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-slate-900 leading-[1.15] tracking-tight">
              Where Early Learning Feels Like{' '}
              <span className="text-rainbow relative inline-block">
                Magical Play!
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-kiki-yellow/80" viewBox="0 0 100 20" preserveAspectRatio="none">
                  <path d="M0 10 Q 50 0, 100 10" stroke="currentColor" strokeWidth="6" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl font-medium leading-relaxed">
              Step into the warm, joyful universe of <strong className="text-slate-900 font-bold">Miss Kiki</strong>. 
              Delightful early maths adventures, sensory bubble popping, and interactive games built with zero ads, 
              zero distractions, and complete peace of mind for parents.
            </p>

            {/* Quick CTAs */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
              <a
                href="#playground"
                onClick={() => sounds.playSound('pop')}
                className="btn-bouncy px-7 py-4 rounded-3xl bg-kiki-coral hover:bg-rose-500 text-white font-heading font-bold text-lg shadow-bouncy flex items-center gap-2.5"
              >
                <PlayIcon className="w-5 h-5 text-white" />
                <span>Play Free Mini-Games</span>
              </a>

              <a
                href="#apps"
                onClick={() => sounds.playSound('tap')}
                className="btn-bouncy px-7 py-4 rounded-3xl bg-white hover:bg-kiki-sky/40 border-2 border-slate-200 hover:border-kiki-blue text-slate-800 font-heading font-bold text-lg shadow-sm flex items-center gap-2"
              >
                <span>View iOS Apps</span>
                <span className="text-kiki-blue">→</span>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="pt-4 grid grid-cols-3 gap-3 max-w-lg mx-auto lg:mx-0">
              <div className="flex flex-col items-center lg:items-start p-3 rounded-2xl bg-white/70 border border-slate-200/80 shadow-xs">
                <span className="text-lg">🚫 📱</span>
                <span className="font-heading font-bold text-xs text-slate-800 mt-1">100% Ad-Free</span>
                <span className="text-[11px] text-slate-500">No popups or trackers</span>
              </div>
              <div className="flex flex-col items-center lg:items-start p-3 rounded-2xl bg-white/70 border border-slate-200/80 shadow-xs">
                <span className="text-lg">✈️ 🔒</span>
                <span className="font-heading font-bold text-xs text-slate-800 mt-1">Offline First</span>
                <span className="text-[11px] text-slate-500">No internet needed</span>
              </div>
              <div className="flex flex-col items-center lg:items-start p-3 rounded-2xl bg-white/70 border border-slate-200/80 shadow-xs">
                <span className="text-lg">💛 🛡️</span>
                <span className="font-heading font-bold text-xs text-slate-800 mt-1">Kid-Safe</span>
                <span className="text-[11px] text-slate-500">COPPA compliant</span>
              </div>
            </div>

          </div>

          {/* Right Column: Interactive Miss Kiki Avatar Card */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-md">
              
              {/* Floating speech bubble */}
              <div className="absolute -top-6 -left-2 sm:-left-6 z-20 bg-white rounded-3xl p-4 shadow-xl border-2 border-kiki-yellow max-w-[260px] animate-float">
                <div className="flex items-center gap-1.5 mb-1">
                  <StarIcon className="w-4 h-4 text-kiki-yellow" />
                  <span className="text-xs font-heading font-bold text-slate-700">Miss Kiki says:</span>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-slate-700 leading-snug">
                  "Hello little learner! Ready to explore our islands and play today?"
                </p>
                <div className="absolute -bottom-2.5 left-10 w-4 h-4 bg-white border-b-2 border-r-2 border-kiki-yellow transform rotate-45" />
              </div>

              {/* Main Illustration Container */}
              <div className="relative rounded-[40px] bg-gradient-to-tr from-kiki-sky via-white to-kiki-mint p-4 border-4 border-white shadow-2xl overflow-hidden group">
                <div className="rounded-[32px] bg-white/80 p-6 flex flex-col items-center text-center">
                  
                  {/* Miss Kiki Character Art */}
                  <div className="relative w-56 h-64 sm:w-64 sm:h-72 flex items-center justify-center">
                    <img
                      src={kikiState}
                      alt="Miss Kiki"
                      className="w-full h-full object-contain filter drop-shadow-md transition-all duration-300 transform group-hover:scale-105"
                    />
                  </div>

                  {/* Character Info & Interactive Voice Button */}
                  <div className="mt-3 w-full">
                    <h2 className="font-heading font-bold text-xl text-slate-800 flex items-center justify-center gap-2">
                      <span>Miss Kiki</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-kiki-green/20 text-emerald-800 font-bold">
                        Friendly Teacher
                      </span>
                    </h2>
                    <p className="text-xs text-slate-500 mt-1 mb-4">
                      Loved by families across English, Telugu &amp; Tamil speaking homes!
                    </p>

                    <button
                      onClick={handleKikiGreeting}
                      className="btn-bouncy w-full py-3 px-4 rounded-2xl bg-kiki-yellow hover:bg-amber-400 text-amber-950 font-heading font-bold text-sm shadow-md flex items-center justify-center gap-2"
                    >
                      <SparklesIcon className="w-5 h-5 text-amber-800" />
                      <span>{greetingPlayed ? 'Tap Again to Hear Miss Kiki!' : 'Tap to Say Hi to Miss Kiki!'}</span>
                    </button>
                  </div>

                </div>
              </div>

              {/* Floating Animal Preview Badges */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-2 shadow-lg border-2 border-kiki-sky flex items-center gap-2">
                <img src="/assets/images/animal-cow.png" alt="Cow" className="w-9 h-9 object-contain" />
                <span className="text-xs font-heading font-bold text-slate-700 pr-2">Moo moo!</span>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl p-2 shadow-lg border-2 border-kiki-sky flex items-center gap-2">
                <img src="/assets/images/pet-pip.png" alt="Pip" className="w-9 h-9 object-contain" />
                <span className="text-xs font-heading font-bold text-slate-700 pr-2">Pip the bird!</span>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
